/** Panel de filtros movil: pantalla completa blanca, cabecera y footer fijos. */
import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { MobileButton } from './MobileButton';
import { MobileIcon } from './MobileIcon';

type MobileFilterSheetProps = {
  open: boolean;
  title?: string;
  onClose: () => void;
  onApply: () => void;
  onReset: () => void;
  applyDisabled?: boolean;
  children: ReactNode;
};

export function MobileFilterSheet({
  open,
  title = 'Filtros',
  onClose,
  onApply,
  onReset,
  applyDisabled = false,
  children,
}: MobileFilterSheetProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const previousFocus = useRef<Element | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    previousFocus.current = document.activeElement;
    closeRef.current?.focus();
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onCloseRef.current();
    }
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener('keydown', onKey);
      if (previousFocus.current instanceof HTMLElement) previousFocus.current.focus();
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-[80] flex h-dvh w-full flex-col overflow-hidden bg-white"
    >
      <header className="safe-area-top flex shrink-0 items-center justify-between gap-3 border-b border-border-light bg-white px-4 py-3">
        <h2 className="min-w-0 flex-1 truncate text-lg font-semibold text-text-heading">{title}</h2>
        <button
          ref={closeRef}
          type="button"
          onClick={() => onCloseRef.current()}
          aria-label="Cerrar filtros"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-text-secondary transition-colors hover:bg-bg-tertiary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <MobileIcon name="close" className="h-5 w-5" />
        </button>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain bg-white px-4 py-4">
        <div className="space-y-5">{children}</div>
      </div>

      <footer className="safe-area-bottom shrink-0 border-t border-border-light bg-white px-4 py-3">
        <div className="grid grid-cols-2 gap-2">
          <MobileButton variant="secondary" onClick={onReset}>
            Restablecer
          </MobileButton>
          <MobileButton onClick={onApply} disabled={applyDisabled}>
            Aplicar
          </MobileButton>
        </div>
      </footer>
    </div>,
    document.body,
  );
}
